import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useState, type FormEvent, type MouseEvent, type ReactNode } from 'react'
import { ApiError } from '../../api/client'
import {
  agentSettingsQuery,
  billingQuery,
  companiesQuery,
  companyEventsQuery,
  companyModulesQuery,
  companySettingsQuery,
  createCompany,
  customersShellQuery,
  departmentQuery,
  departmentsQuery,
  groupQuery,
  groupsQuery,
  isCustomersTab,
  newCompanyQuery,
  newOfficeQuery,
  officeAutoCreateQuery,
  officeIpsQuery,
  officeNetworksQuery,
  officeQuery,
  officesQuery,
  peopleQuery,
  removeDepartment,
  removeOffice,
  saveAgentSettings,
  saveBilling,
  saveCnrs,
  saveCompanyModules,
  saveCompanySettings,
  uploadCustomerLogo,
  saveDepartment,
  saveDomains,
  saveGroup,
  saveGroupCompanies,
  saveOffice,
  saveOfficeAdmins,
  saveOfficeAutoCreate,
  saveOfficeDepartments,
  saveOfficeIp,
  saveOfficeNetwork,
  saveTwoFactorIps,
  saveVips,
  setCompanyStatus,
  setTwoFactorStatus,
  statusWarningQuery,
  twoFactorQuery,
  type BillingForm,
  type CustomersPanelTab,
  type CustomersTab,
} from '../../api/settingsCustomers'
import { contractsShellQuery } from '../../api/settingsContracts'
import { ContractsPanel } from '../contracts/ContractsSettingsPage'
import { CompanySiemGroupCard } from './CompanySiemGroupCard'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { Forbidden } from '../../ui/Forbidden'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { SelectPicker } from '../../ui/SelectPicker'
import { Spinner } from '../../ui/Spinner'
/* Grila comuna a formularelor din randul desfacut (`.tk-action-form`): eticheta
   pe un sfert, la dreapta, campul pe rest, cu greutate peste `form-inline`-ul
   invelisului DataTables. Aceeasi foaie ca la tichet, nu o a treia grila. */
import '../../easeedesk/TicketActionForm.css'

/**
 * Settings -> Customers: firmele-client, grupurile lor, si fisa fiecarei firme.
 *
 * Perechile vechi: `views/version-3/settings/customers/*` si
 * `public/js/settings/customers.js` (`CustomerCompanies`, `CustomerGroups`,
 * `CustomersActions`).
 *
 * CE POATE ECRANUL
 * ----------------
 *   Companies        lista, firma noua, si randul desfasurat cu opt file:
 *                    Events, Departments, Offices, Settings, Agent Settings,
 *                    Company Modules, Two Factor Authentication, Billing Data
 *   Customer Groups  lista, grup nou, redenumire, si firmele grupului
 *
 * CE NU S-A MUTAT, SI DE CE
 * -------------------------
 *  - SCHIMBAREA SIGLEI. Fereastra veche trimitea la
 *    `settings/companies/company_logo`, iar metoda din acest controller era
 *    comentata de ani. Aceeasi hotarare ca la Settings -> Company: incarcarile
 *    de imagine sunt un lant separat, inca nemutat. Butonul nu se deseneaza -
 *    unul care nu duce nicaieri e mai rau decat lipsa lui.
 *  - Fila "Contracts" din randul desfasurat: e ecranul Contracts, care are ruta
 *    lui (`/settings/customersV2/contracts`), si se deschide de acolo.
 *
 * Butoanele care scriu se arata dupa ce spune serverul (`canCreate`,
 * `canEdit`); poarta ramane pe server, asta doar scuteste un 403 la apasare.
 *
 * DESENUL RANDULUI DESFACUT (11 septembrie 2026)
 * ----------------------------------------------
 * Acelasi ca la tichet, CMDB si catalog: filele in coloana din stanga
 * (`row d-flex m-0` > `col-auto bg-lightest-gray` > `ul.action_tabs`),
 * formularele pe grila comuna `.tk-action-form` (`Field`: eticheta pe un sfert,
 * la dreapta, campul pe rest; `SubmitRow`: butonul la dreapta), sertarele pe
 * `SelectPicker`. Grila are `body` in fata fiindca randul sta in invelisul
 * DataTables cu `form-inline`, iar in productie `app.css` vine DUPA pachet.
 */

const COMPANIES_TABLE = 'settings-customers-companies'

const GROUPS_TABLE = 'settings-customers-groups'

export function esc(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

/** Ce spune SERVERUL cand refuza - "A department with the same name already exists!". */
function failed(error: unknown): string {
  return error instanceof ApiError && error.message ? error.message : 'Something went wrong.'
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** `moment(date).format('D MMM YYYY, HH:mm')`, ca in `CustomerGroups`. */
export function whenText(value: string): string {
  const when = new Date(String(value).replace(' ', 'T'))
  if (Number.isNaN(when.getTime())) return ''
  const hh = String(when.getHours()).padStart(2, '0')
  const mm = String(when.getMinutes()).padStart(2, '0')
  return `${when.getDate()} ${MONTHS[when.getMonth()]} ${when.getFullYear()}, ${hh}:${mm}`
}

/* -------------------------------------------------------------------------
 * Coloanele celor doua liste
 * ---------------------------------------------------------------------- */

/** `CustomerCompanies.companiesModel`, coloana cu coloana. */
export function companyColumns(): TableColumn[] {
  return [
    {
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 50,
      className: 'metroTd',
      html: (row) =>
        `<center class="show_details color-dark-grey pointer font-16" data-id="${Number(row.ID)}">` +
        '<i class="fas fa-plus-circle"></i></center>',
    },
    {
      index: 1,
      title: 'Name',
      orderable: true,
      visible: true,
      width: 200,
      className: 'metroTd',
      html: (row) => (String(row.Nume ?? '') !== '' ? esc(String(row.Nume)) : '<i class="fas fa-minus font-10"></i>'),
    },
    {
      index: 2,
      title: 'Status',
      orderable: true,
      visible: true,
      className: 'metroTd',
      html: (row) =>
        Number(row.Status) === 1
          ? '<center><i class="fas fa-check-circle font-18 color-green" data-placement="top" data-toggle="tooltip" data-original-title="Company Enabled"> </i></center>'
          : '<center><i class="fas fa-minus-circle font-18 color-red" data-placement="top" data-toggle="tooltip" data-original-title="Company Disabled"> </i></center>',
    },
    {
      /* Apare in selectorul de firme din antet? Daca nu, de ce - si, cand e
         doar inactiva, butonul de pornit chiar aici. */
      index: 3,
      title: 'In selector?',
      orderable: false,
      visible: true,
      className: 'metroTd',
      html: (row) => {
        if (row.inSelector) return '<i class="fas fa-check color-green"></i> Yes'
        const why = esc(String(row.whyNot ?? 'no'))
        if (row.whyNot === 'inactive' && row.canEnable) {
          return (
            '<span class="color-red">Inactive</span> ' +
            `<button type="button" class="btn btn-xs btn-green od-enable-company" data-id="${Number(row.ID)}">Enable</button>`
          )
        }
        return `<span class="color-red">No</span> <span class="color-light-grey">- ${why}</span>`
      },
    },
    { index: 4, title: 'Devices', orderable: true, visible: true, className: 'metroTd', html: (row) => String(row.Devices ?? 0) },
    { index: 5, title: 'Users', orderable: true, visible: true, className: 'metroTd', html: (row) => String(row.Users ?? 0) },
    { index: 6, title: 'Offices', orderable: true, visible: true, className: 'metroTd', html: (row) => String(row.Offices ?? 0) },
    { index: 7, title: 'Departments', orderable: true, visible: true, className: 'metroTd', html: (row) => String(row.Departments ?? 0) },
    {
      index: 8,
      title: 'IT Manager',
      orderable: true,
      visible: true,
      className: 'metroTd',
      html: (row) => (row.itManager ? esc(String(row.itManager)) : '<i class="fas fa-minus font-10"></i>'),
    },
    {
      index: 9,
      title: 'System Administrator',
      orderable: true,
      visible: true,
      className: 'metroTd',
      html: (row) => (row.SystemAdmin ? esc(String(row.SystemAdmin)) : '<i class="fas fa-minus font-10"></i>'),
    },
    { index: 10, title: 'Agent Version', orderable: true, visible: true, className: 'metroTd', html: (row) => esc(String(row.agentVersion ?? '')) },
  ]
}

export const COMPANY_ORDER_NAMES = [
  '',
  'firma.Nume',
  'firma.Deleted',
  '',
  'Devices',
  'Users',
  'Offices',
  'Departments',
  'itManager',
  'SystemAdmin',
  'agentVersion',
]

/** `CustomerGroups.groupsModel`. */
export function groupColumns(): TableColumn[] {
  return [
    {
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 50,
      className: 'metroTd',
      html: (row) =>
        `<center class="show_details color-dark-grey pointer font-16" data-id="${Number(row.ID)}">` +
        '<i class="fas fa-plus-circle"></i></center>',
    },
    { index: 1, title: 'Group name', orderable: true, visible: true, className: 'metroTd', html: (row) => esc(String(row.group_name ?? '')) },
    { index: 2, title: 'Assigned companies', orderable: true, visible: true, className: 'metroTd', html: (row) => String(row.companies ?? 0) },
    { index: 3, title: 'Created by', orderable: true, visible: true, className: 'metroTd', html: (row) => esc(String(row.createdBy ?? '')) },
    { index: 4, title: 'Created', orderable: true, visible: true, className: 'metroTd', html: (row) => esc(whenText(String(row.date ?? ''))) },
  ]
}

export const GROUP_ORDER_NAMES = [
  '',
  'it_companies_groups.group_name',
  'companies',
  'angajati.Prenume',
  'it_companies_groups.date',
]

/* -------------------------------------------------------------------------
 * Ecranul
 * ---------------------------------------------------------------------- */

export function CustomersSettingsPage({ tab, openCompany = 0, openPanel = '' }: { tab: string; openCompany?: number; openPanel?: string }) {
  const navigate = useNavigate()
  const shell = useQuery(customersShellQuery)

  useEffect(() => {
    if (!shell.data) return
    if (isCustomersTab(tab)) return
    void navigate({ to: '/settings/customers/$tab', params: { tab: 'companies' } })
  }, [shell.data, tab, navigate])

  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />
  if (!shell.data) return <Spinner />

  const current = isCustomersTab(tab) ? tab : 'companies'
  const groups: string[] = []
  for (const one of shell.data.tabs) if (!groups.includes(one.group)) groups.push(one.group)

  return (
    <>
      <nav className="od-tabbar">
        <ul className="od-tabbar-nav">
          {groups.map((group) => (
            <Fragmented key={group}>
              <li className="menu-header">{group}</li>
              {shell.data.tabs
                .filter((one) => one.group === group)
                .map((one) => (
                  <TabLink key={one.id} tab={one} current={current} />
                ))}
            </Fragmented>
          ))}
          <li className="menu-block-divider"></li>
        </ul>
      </nav>
      <div className="page-content-wrapper">
        {!shell.data.ready ? (
          <div className="alert alert-info">
            <i className="fas fa-info-circle" aria-hidden="true"></i> Customers are not set up on this instance.
          </div>
        ) : null}
        {current === 'companies' ? (
          <CompaniesPanel
            canCreate={shell.data.canCreate}
            panelTabs={shell.data.panelTabs}
            openCompany={openCompany}
            openPanel={openPanel}
          />
        ) : (
          <GroupsPanel />
        )}
      </div>
    </>
  )
}

/** `<>...</>` cu cheie: un `<li>` header plus filele lui, in aceeasi lista. */
function Fragmented({ children }: { children: ReactNode }) {
  return <>{children}</>
}

/**
 * O fila din bara. Cele doua de licente duc la ecranul LOR, care e deja mutat -
 * de-aia se deosebesc dupa adresa, nu dupa un camp in plus.
 */
function TabLink({ tab, current }: { tab: CustomersTab; current: string }) {
  const navigate = useNavigate()
  const own = tab.href.startsWith('settings/customers/')
  const active = own && tab.id === current

  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    if (own) {
      void navigate({ to: '/settings/customers/$tab', params: { tab: tab.id } })
      return
    }
    void navigate({ to: '/settings/licenses/$tab', params: { tab: tab.id } })
  }

  return (
    <li className={active ? 'menu-tab active' : 'menu-tab'}>
      <a id={tab.id} href={'/app/' + tab.href} onClick={onClick}>
        <i className={tab.icon} aria-hidden="true"></i> {tab.label}
      </a>
    </li>
  )
}

/* -------------------------------------------------------------------------
 * Companies
 * ---------------------------------------------------------------------- */

function CompaniesPanel({
  canCreate,
  panelTabs,
  openCompany = 0,
  openPanel = '',
}: {
  canCreate: boolean
  panelTabs: CustomersPanelTab[]
  openCompany?: number
  openPanel?: string
}) {
  const query = useQueryClient()
  const [state, setState] = useTableState(COMPANIES_TABLE, {
    start: 0,
    length: 25,
    /* `aaSorting: [[2, 'desc']]` din customers.js - coloana Status. */
    order: { column: 2, dir: 'desc' },
    search: '',
  })
  const columns = useMemo(() => companyColumns(), [])
  const [creating, setCreating] = useState(false)
  const [status, setStatusFor] = useState<{ id: number; on: boolean } | null>(null)

  const table = useQuery({
    ...companiesQuery({
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: state.order.column,
      orderName: COMPANY_ORDER_NAMES[state.order.column] ?? 'firma.Nume',
      orderDir: state.order.dir,
      search: state.search,
    }),
    placeholderData: keepPreviousData,
  })

  if (table.error instanceof ApiError && table.error.status === 403) return <Forbidden />

  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]
  const reload = () => void query.invalidateQueries({ queryKey: ['settings', 'customers'] })

  return (
    <div
      onClick={(event) => {
        /* Butonul "Enable" e HTML scris de coloana, ca in DataTables, deci nu
           are React pe el - un singur ascultator delegat, ca in portal. */
        const button = (event.target as HTMLElement).closest('.od-enable-company')
        if (!button) return
        const id = Number(button.getAttribute('data-id') ?? 0)
        if (id > 0) setStatusFor({ id, on: true })
      }}
    >
      {canCreate && (
        <button type="button" id="odCreateCompany" className="pull-right btn btn-success btn-sm" onClick={() => setCreating(true)}>
          <i className="fas fa-plus" aria-hidden="true"></i> Create company
        </button>
      )}
      <h4>
        <strong>Companies</strong>
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage <strong>companies</strong>
      </label>
      {/* `?company=7&panel=offices` (iconita de sedii cu nume implicit din bara):
          fisa firmei deschisa deasupra listei, pe fila ceruta - firma poate sta
          pe alta pagina a tabelului, deci nu se cauta randul ei. */}
      {openCompany > 0 && (
        <div className="panel panel-default" data-testid="open-company-panel" style={{ marginTop: 10 }}>
          <div className="panel-heading">
            <strong>{openedName(rows, openCompany)}</strong>
          </div>
          <CompanyPanel
            companyId={openCompany}
            tabs={panelTabs}
            initialTab={openPanel}
            onStatus={(on) => setStatusFor({ id: openCompany, on })}
          />
        </div>
      )}
      <DataTable
        id={COMPANIES_TABLE}
        columns={columns}
        rows={rows}
        total={table.data?.recordsFiltered ?? 0}
        fetching={table.isFetching}
        error={table.error ? table.error.message : null}
        onRetry={() => void table.refetch()}
        state={state}
        onState={setState}
        rowId={(row) => String(row.DT_RowId ?? `row_${Number(row.ID)}`)}
        emptyHint="A company here is a customer of yours."
        details={(row) => (
          <CompanyPanel
            companyId={Number(row.ID)}
            tabs={panelTabs}
            onStatus={(on) => setStatusFor({ id: Number(row.ID), on })}
          />
        )}
      />
      <CreateCompanyModal open={creating} onClose={() => setCreating(false)} onSaved={reload} />
      <CompanyStatusModal
        companyId={status?.id ?? 0}
        enable={status?.on ?? false}
        onClose={() => setStatusFor(null)}
        onSaved={() => {
          setStatusFor(null)
          reload()
        }}
      />
    </div>
  )
}

/** `companies/add_company.php` + `CustomersActions.submitModal`. */
function CreateCompanyModal({ open, onClose, onSaved }: { open: boolean; onClose(): void; onSaved(): void }) {
  const form = useQuery({ ...newCompanyQuery, enabled: open })
  const [fields, setFields] = useState({
    company_name: '',
    main_office: '',
    siem_group: '',
    contact_person: '',
    it_manager: '',
    language: 'EN',
    unique_registartion: '',
    tax_identification: '',
    latitude: '',
    longitude: '',
    working_program: '',
  })
  const [domains, setDomains] = useState<string[]>([''])
  const [modules, setModules] = useState<number[]>([])
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) return
    setError('')
    setBusy(false)
    setDomains([''])
    setModules([])
    setFields({
      company_name: '',
      main_office: '',
      siem_group: '',
      contact_person: '',
      it_manager: '',
      language: 'EN',
      unique_registartion: '',
      tax_identification: '',
      latitude: '',
      longitude: '',
      working_program: '',
    })
  }, [open])

  /* Firma noua porneste cu modulele implicite bifate (Overview, Inventoree,
     Softsee, Opteemum, Proactivitee, Easeedesk, Settings); restul raman oprite. */
  useEffect(() => {
    if (!open || !form.data) return
    setModules(form.data.modules.filter((one) => one.default === true).map((one) => one.id))
  }, [open, form.data])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError('')
    void createCompany({ ...fields, domains: domains.filter((one) => one.trim() !== ''), modules })
      .then(() => {
        setBusy(false)
        toast('Company created.', 'Success')
        onSaved()
        onClose()
      })
      .catch((err: unknown) => {
        setBusy(false)
        setError(failed(err))
      })
  }

  const text = (name: keyof typeof fields, label: string, required = false, type = 'text') => (
    <div className="controls">
      <label className="control-label" style={{ textAlign: 'left' }} htmlFor={`new-company-${name}`}>
        {label} {required ? <span className="required">*</span> : null}
      </label>
      <input
        id={`new-company-${name}`}
        type={type}
        className="form-control"
        name={String(name)}
        required={required}
        value={fields[name]}
        onChange={(e) => setFields({ ...fields, [name]: e.target.value })}
      />
    </div>
  )

  return (
    <Modal id="addCompanyModal" open={open} onClose={onClose} title="Create Company" large>
      <form className="form-horizontal" id="add_new_company" onSubmit={submit}>
        {error !== '' ? (
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-circle" aria-hidden="true"></i> {error}
          </div>
        ) : null}
        <b>
          <i className="fas fa-pencil-alt" aria-hidden="true"></i> General Details
        </b>
        <div className="col-md-12" style={{ marginTop: 15 }}>
          <div className="control-group">
            {text('company_name', 'Company Name:', true)}
            {text('main_office', 'Main Office:', true)}
            <div className="controls">
              <label className="control-label" style={{ textAlign: 'left' }} htmlFor="new-company-siem_group">
                SIEM group:
              </label>
              <select
                id="new-company-siem_group"
                className="form-control"
                name="siem_group"
                value={fields.siem_group}
                onChange={(e) => setFields({ ...fields, siem_group: e.target.value })}
              >
                <option value="">No SIEM group</option>
                {(form.data?.siemGroups ?? []).map((one) => (
                  <option key={one.ID} value={one.ID}>
                    {one.name}
                  </option>
                ))}
              </select>
              <p className="help-block">Where XDR sends this company&apos;s security events.</p>
            </div>
            {text('contact_person', 'Contact Person:')}
            <div className="controls">
              <label className="control-label" style={{ textAlign: 'left' }} htmlFor="new-company-manager">
                IT Manager:
              </label>
              <select
                id="new-company-manager"
                className="form-control"
                name="it_manager"
                value={fields.it_manager}
                onChange={(e) => setFields({ ...fields, it_manager: e.target.value })}
              >
                <option value="">Select..</option>
                {(form.data?.itManagers ?? []).map((one) => (
                  <option key={one.ID} value={one.ID}>
                    {one.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="controls">
              <label className="control-label" style={{ textAlign: 'left' }} htmlFor="new-company-language">
                Language:
              </label>
              <select
                id="new-company-language"
                className="form-control"
                name="language"
                value={fields.language}
                onChange={(e) => setFields({ ...fields, language: e.target.value })}
              >
                <option value="EN">EN</option>
                <option value="RO">RO</option>
              </select>
            </div>
            {text('unique_registartion', 'Unique registration code:')}
            {text('tax_identification', 'Tax identification number:')}
            {text('latitude', 'Latitude:', false, 'number')}
            {text('longitude', 'Longitude:', false, 'number')}
          </div>
          <div className="control-group">
            <div className="controls">
              <label className="form-label" htmlFor="new-company-program">
                Working program
              </label>
              <textarea
                id="new-company-program"
                className="form-control"
                name="working_program"
                rows={4}
                value={fields.working_program}
                onChange={(e) => setFields({ ...fields, working_program: e.target.value })}
              />
            </div>
          </div>
          <div className="control-group">
            <div className="controls">
              <i className="fa fa-tasks" aria-hidden="true"></i>
              <b> Domains</b>
              <RepeatedInputs values={domains} onChange={setDomains} placeholder="domain.com" name="domains" />
            </div>
          </div>
          <div className="control-group">
            <div className="controls">
              <b>Modules</b>
              <div className="input-group">
                {(form.data?.modules ?? []).map((one) => (
                  <label className="col-md-3" key={one.id} htmlFor={`new-company-module-${one.id}`}>
                    {one.name}&nbsp;
                    <ModuleCheck
                      id={`new-company-module-${one.id}`}
                      checked={modules.includes(one.id)}
                      onChange={(on) => setModules(on ? [...modules, one.id] : modules.filter((x) => x !== one.id))}
                    />
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-12 aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button type="submit" className="btn btn-primary add_company" disabled={busy}>
            Save
          </button>
        </div>
        {/*
         * Cele doua `col-md-12` de mai sus plutesc (`float: left` de la 992px),
         * deci nu dau inaltime formularului: fara randul asta `.modal-content`
         * se oprea sub "General Details" (118px) si toate campurile curgeau sub
         * el, peste tabel si peste subsolul paginii. In portal inchidea
         * plutirea `.modal-footer`-ul gol de dupa ele; `Modal` nu-l deseneaza
         * cand n-are butoane de pus in el.
         */}
        <div className="clearfix"></div>
      </form>
    </Modal>
  )
}

/**
 * Bifa unui modul din "Create Company", desenata ca in portal.
 *
 * `add_company.php` scrie `<input class="form-control iCheckModulePremium
 * iCheckM modules" type="checkbox">`, iar `customers.js` o trece prin iCheck
 * cu `checkboxClass: 'icheckbox_flat-green'`: patratul verde de 20px din
 * sprite, langa nume. In v4 nu ruleaza pluginul, deci ramanea casuta
 * browserului - 15px, in cerneala zonei (`od_console.css`, sectiunea 5) - si
 * fereastra nu mai semana cu cea veche.
 *
 * Invelisul e cel pe care il scrie iCheck (`div.icheckbox_flat-green`, cu
 * `checked` cand e bifata), stilul lui e deja in `app.css`; `od_console.css`
 * schimba sprite-ul numai la `flat-red`, deci verdele ramane verde, ca in
 * portal. Inputul adevarat sta deasupra, transparent, cum il lasa si iCheck:
 * clicul si tastatura ajung tot la el. Aceeasi forma ca `Check` din
 * `inventoree/UnassignedCard.tsx`.
 */
function ModuleCheck({ id, checked, onChange }: { id: string; checked: boolean; onChange(on: boolean): void }) {
  return (
    <div
      className={`icheckbox_flat-green${checked ? ' checked' : ''}`}
      style={{ position: 'relative', verticalAlign: 'middle' }}
    >
      <input
        id={id}
        type="checkbox"
        className="iCheckModulePremium iCheckM modules"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, margin: 0, cursor: 'pointer' }}
      />
    </div>
  )
}

/**
 * Randul de casute cu `+` si `x` din machete (`addInput` / `removeInput`),
 * fara clonarea de DOM: aici e o lista, si butoanele o schimba.
 */
function RepeatedInputs({
  values,
  onChange,
  placeholder,
  name,
}: {
  values: string[]
  onChange(next: string[]): void
  placeholder: string
  name: string
}) {
  const rows = values.length === 0 ? [''] : values

  return (
    <>
      {rows.map((one, index) => (
        <div className="input-group" key={`${name}-${index}`}>
          <input
            className="form-control small input-inline domains"
            type="text"
            placeholder={placeholder}
            aria-label={placeholder}
            value={one}
            onChange={(e) => {
              const next = [...rows]
              next[index] = e.target.value
              onChange(next)
            }}
          />
          <div className="input-group-btn">
            <button
              type="button"
              className="btn btn-default color-red removeInput"
              title="Remove"
              onClick={() => onChange(rows.filter((_, i) => i !== index))}
            >
              <i className="fas fa-times" aria-hidden="true"></i>
            </button>
          </div>
          <div className="input-group-btn">
            <button
              type="button"
              className="btn btn-default color-green2 addInput"
              title="Add"
              onClick={() => onChange([...rows.slice(0, index + 1), '', ...rows.slice(index + 1)])}
            >
              <i className="fas fa-plus" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      ))}
    </>
  )
}

/** `others/change_company_status.php`: intreaba, si spune cate aparate pierde. */
function CompanyStatusModal({
  companyId,
  enable,
  onClose,
  onSaved,
}: {
  companyId: number
  enable: boolean
  onClose(): void
  onSaved(): void
}) {
  const warning = useQuery({ ...statusWarningQuery(companyId), enabled: companyId > 0 && !enable })
  const [busy, setBusy] = useState(false)
  const devices = warning.data?.devices ?? 0

  return (
    <Modal id="modal-status" open={companyId > 0} onClose={onClose} title="Change Company Status">
      <p>
        {enable ? (
          'Are you sure you want to enable this company?'
        ) : devices > 0 ? (
          <>
            There are <b>{devices}</b> devices with installed agent! Are you sure you want to disable this company?
          </>
        ) : (
          'Are you sure you want to disable this company?'
        )}
      </p>
      <div className="form-group aright">
        <button type="button" className="btn btn-default" onClick={onClose}>
          Close
        </button>{' '}
        <button
          type="button"
          className="btn btn-primary update_company_status"
          disabled={busy}
          onClick={() => {
            setBusy(true)
            void setCompanyStatus(companyId, enable ? 1 : 0)
              .then(() => {
                setBusy(false)
                toast(enable ? 'Company enabled. It is in the company selector now.' : 'Company disabled.', 'Success')
                onSaved()
              })
              .catch((error: unknown) => {
                setBusy(false)
                toast(failed(error), 'Error', 'error')
              })
          }}
        >
          Save
        </button>
      </div>
    </Modal>
  )
}

/* -------------------------------------------------------------------------
 * Randul desfasurat al unei firme
 * ---------------------------------------------------------------------- */

/** Numele firmei deschise din adresa, daca randul ei e pe pagina; altfel numarul. */
function openedName(rows: Record<string, unknown>[], companyId: number): string {
  const row = rows.find((one) => Number(one.ID) === companyId)
  const name = row ? String(row.Nume ?? '').trim() : ''
  return name !== '' ? name : `Company #${companyId}`
}

function CompanyPanel({
  companyId,
  tabs,
  initialTab = '',
  onStatus,
}: {
  companyId: number
  tabs: CustomersPanelTab[]
  initialTab?: string
  onStatus(enable: boolean): void
}) {
  const [tab, setTab] = useState<string | null>(tabs.some((one) => one.id === initialTab) ? initialTab : null)
  const active = tab ?? tabs[0]?.id ?? null

  if (active === null) return <Forbidden />

  /* Acelasi invelis ca la tichet, CMDB si catalog: meniul filelor in coloana
     din stanga (`col-auto bg-lightest-gray`), continutul pe rest. */
  return (
    <div className="row d-flex m-0">
      <div className="col-auto bg-lightest-gray">
        <ul className="action_tabs">
          {tabs.map((one) => (
            <li key={one.id}>
              <a
                href="/app/settings/customers/companies"
                className={one.id === active ? 'action_button active' : 'action_button'}
                onClick={(e) => {
                  e.preventDefault()
                  setTab(one.id)
                }}
              >
                {one.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className="col-md-12">
        <div className="details_content">
          {active === 'events' ? <EventsTab companyId={companyId} /> : null}
          {active === 'departments' ? <DepartmentsTab companyId={companyId} /> : null}
          {active === 'offices' ? <OfficesTab companyId={companyId} /> : null}
          {active === 'settings' ? <CompanySettingsTab companyId={companyId} onStatus={onStatus} /> : null}
          {active === 'contracts' ? <ContractsTab companyId={companyId} /> : null}
          {active === 'agent_settings' ? <AgentSettingsTab companyId={companyId} /> : null}
          {active === 'modules' ? <ModulesTab companyId={companyId} /> : null}
          {active === 'two_factor' ? <TwoFactorTab companyId={companyId} /> : null}
          {active === 'billing' ? <BillingTab companyId={companyId} /> : null}
        </div>
      </div>
    </div>
  )
}

/**
 * Un rand al grilei comune: eticheta pe prima coloana, campul (sau campurile)
 * pe a doua - `body .tk-action-form .form-group` din `TicketActionForm.css`.
 */
function Field({
  id,
  label,
  required = false,
  children,
}: {
  id?: string
  label: ReactNode
  required?: boolean
  children: ReactNode
}) {
  return (
    <div className="form-group">
      <label className="control-label" htmlFor={id}>
        {label}
        {required ? (
          <>
            {' '}
            <span className="required">*</span>
          </>
        ) : null}
      </label>
      {children}
    </div>
  )
}

/** Butonul de trimitere, la dreapta, pe randul lui (`tk-form-submit`). */
function SubmitRow({ children }: { children: ReactNode }) {
  return <div className="form-group tk-form-submit">{children}</div>
}

/** `companies/events.php`. */
function EventsTab({ companyId }: { companyId: number }) {
  const page = useQuery(companyEventsQuery(companyId))

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />

  if (page.data.events.length === 0) {
    return <div className="alert alert-info">No event available!</div>
  }

  return (
    <>
      <i>(*last 100 changes)</i>
      <ul className="events-list">
        {page.data.events.map((day) => (
          <Fragmented key={day.day}>
            <li className="event-data">
              <i className="far fa-calendar-check" aria-hidden="true"></i> {day.day}
            </li>
            {day.rows.map((one, index) => (
              <li className="event-details" key={`${day.day}-${index}`}>
                {one.time}{' '}
                <span className="color-blue">
                  <i className="fas fa-pencil-alt color-blue" aria-hidden="true"></i>{' '}
                  {/* Textul notei e HTML scris de portal (`<b>Nume</b>`), nu de om. */}
                  <span dangerouslySetInnerHTML={{ __html: one.details }} />
                </span>
              </li>
            ))}
          </Fragmented>
        ))}
      </ul>
    </>
  )
}

/** Coloana din stanga cu "New ..." rosu si randurile existente (`nav-metro-tabs`). */
function SideList({
  newLabel,
  items,
  chosen,
  onPick,
}: {
  newLabel: string
  items: { id: number; label: string }[]
  chosen: number | null
  onPick(id: number | null): void
}) {
  return (
    <ul className="nav nav-metro-tabs tabs-left">
      <li className={chosen === null ? 'active' : ''}>
        <a
          href="#new"
          style={{ color: '#ff0000' }}
          onClick={(e) => {
            e.preventDefault()
            onPick(null)
          }}
        >
          {newLabel}
        </a>
      </li>
      {items.map((one) => (
        <li key={one.id} className={chosen === one.id ? 'active' : ''}>
          <a
            href={`#item-${one.id}`}
            onClick={(e) => {
              e.preventDefault()
              onPick(one.id)
            }}
          >
            {one.label}
          </a>
        </li>
      ))}
    </ul>
  )
}

/** `companies/departments.php` + `others/{create_department,department_settings}.php`. */
function DepartmentsTab({ companyId }: { companyId: number }) {
  const query = useQueryClient()
  const list = useQuery(departmentsQuery(companyId))
  const [chosen, setChosen] = useState<number | null>(null)

  if (list.error instanceof ApiError && list.error.status === 403) return <Forbidden />
  if (!list.data) return <Spinner />

  const reload = () => void query.invalidateQueries({ queryKey: ['settings', 'customers'] })

  return (
    <div className="row">
      <div className="col-md-2">
        <SideList
          newLabel="New Department"
          items={list.data.departments.map((one) => ({ id: one.ID, label: one.Nume }))}
          chosen={chosen}
          onPick={setChosen}
        />
      </div>
      <div className="col-md-10">
        <div className="nav-content">
          <DepartmentForm
            companyId={companyId}
            departmentId={chosen}
            onSaved={(id) => {
              setChosen(id)
              reload()
            }}
            onRemoved={() => {
              setChosen(null)
              reload()
            }}
          />
        </div>
      </div>
    </div>
  )
}

function DepartmentForm({
  companyId,
  departmentId,
  onSaved,
  onRemoved,
}: {
  companyId: number
  departmentId: number | null
  onSaved(id: number | null): void
  onRemoved(): void
}) {
  const existing = useQuery(departmentQuery(companyId, departmentId ?? 0))
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (departmentId === null) {
      setName('')
      setEmail('')
      return
    }
    if (existing.data) {
      setName(existing.data.department.name)
      setEmail(existing.data.department.email)
    }
  }, [departmentId, existing.data])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    const fields: Record<string, string> = { name, email }
    if (departmentId !== null) fields.id = String(departmentId)
    void saveDepartment(companyId, fields)
      .then((answer) => {
        setBusy(false)
        toast(departmentId === null ? 'Department created.' : 'Department updated.', 'Success')
        onSaved(departmentId ?? answer.id)
      })
      .catch((error: unknown) => {
        setBusy(false)
        toast(failed(error), 'Error', 'error')
      })
  }

  return (
    <div className="col-md-12">
      <div className="portlet light bg-inverse bordered-left border-mauve">
        <div className="portlet-title">
          <div className="caption color-mauve">
            <i className="fas fa-pencil-alt" aria-hidden="true"></i>{' '}
            <span className="caption-subject bold uppercase">
              {departmentId === null ? 'Create Department' : 'Settings'}
            </span>
          </div>
          <div className="actions">
            <button type="submit" form="department_settings" className="btn btn-primary btn-xs" disabled={busy}>
              {departmentId === null ? 'Save' : 'Update'}
            </button>{' '}
            {departmentId === null ? null : (
              <button
                type="button"
                className="btn btn-xs btn-danger remove_department"
                onClick={() => {
                  if (!confirm('Remove this department?')) return
                  void removeDepartment(companyId, departmentId)
                    .then(() => {
                      toast('Department removed.', 'Success')
                      onRemoved()
                    })
                    .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
                }}
              >
                Remove
              </button>
            )}
          </div>
        </div>
        <div className="portlet-body">
          <form method="post" className="tk-action-form" id="department_settings" onSubmit={submit}>
            <Field id="department-name" label="Name" required>
              <input
                id="department-name"
                type="text"
                required
                className="form-control"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            <Field id="department-email" label="Email">
              <input
                id="department-email"
                type="text"
                className="form-control"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
          </form>
        </div>
      </div>
    </div>
  )
}

/** `companies/offices.php` + `others/{create_office,office_settings}.php`. */
function OfficesTab({ companyId }: { companyId: number }) {
  const query = useQueryClient()
  const list = useQuery(officesQuery(companyId))
  const [chosen, setChosen] = useState<number | null>(null)

  if (list.error instanceof ApiError && list.error.status === 403) return <Forbidden />
  if (!list.data) return <Spinner />

  const reload = () => void query.invalidateQueries({ queryKey: ['settings', 'customers'] })

  return (
    <div className="row">
      <div className="col-md-2">
        <div className="scroll-container">
          <SideList
            newLabel="New Office"
            items={list.data.offices.map((one) => ({ id: one.ID, label: one.Name }))}
            chosen={chosen}
            onPick={setChosen}
          />
        </div>
      </div>
      <div className="col-md-10">
        <OfficeAutoCreatePanel companyId={companyId} />
        <div className="nav-content">
          {chosen === null ? (
            <NewOfficeForm
              companyId={companyId}
              onSaved={(id) => {
                setChosen(id)
                reload()
              }}
            />
          ) : (
            <OfficeSettings
              companyId={companyId}
              officeId={chosen}
              onChanged={reload}
              onRemoved={() => {
                setChosen(null)
                reload()
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Comutatorul firmei: sediu "Office N" creat singur pentru o subretea /24
 * necunoscuta. Il aplica ph4.api (`OfficeAutoCreate`) la raportarea agentului;
 * pentru aparatele care exista deja e `/cron/inventoree/office_auto_create`.
 * Stins implicit - fara el nu se scrie nimic.
 */
export function OfficeAutoCreatePanel({ companyId }: { companyId: number }) {
  const query = useQueryClient()
  const page = useQuery(officeAutoCreateQuery(companyId))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  if (!page.data) return null

  const toggle = (on: boolean) => {
    setBusy(true)
    setError('')
    void saveOfficeAutoCreate(companyId, on)
      .then(() => {
        toast(on ? 'Offices will be created automatically.' : 'Automatic offices are off.', 'Success')
        void query.invalidateQueries({ queryKey: ['settings', 'customers', 'office-auto-create', companyId] })
      })
      .catch((err: unknown) => setError(failed(err)))
      .finally(() => setBusy(false))
  }

  return (
    <div className="portlet light bg-inverse bordered-left border-mauve" id="officeAutoCreatePanel">
      <div className="portlet-body">
        {!page.data.tableHere ? (
          <div className="alert alert-info">
            <i className="fas fa-info-circle" aria-hidden="true"></i> The setting table is not created yet - the
            it_office_auto_create migration has not run.
          </div>
        ) : null}
        {error !== '' ? <div className="alert alert-danger">{error}</div> : null}
        <label className="mt-checkbox mt-checkbox-outline" htmlFor="officeAutoCreate">
          <input
            type="checkbox"
            id="officeAutoCreate"
            checked={page.data.enabled}
            disabled={busy || !page.data.canWrite || !page.data.tableHere}
            onChange={(e) => toggle(e.target.checked)}
          />{' '}
          Create offices automatically for unknown networks
          <span></span>
        </label>
        <p className="help-block">
          A device whose internal address is in no office network gets a new office - "Office 1", "Office 2"... - one
          per /24 network, and the network is recorded on it. Rename the offices afterwards.
        </p>
      </div>
    </div>
  )
}

function NewOfficeForm({ companyId, onSaved }: { companyId: number; onSaved(id: number): void }) {
  useQuery(newOfficeQuery(companyId))
  const [name, setName] = useState('')
  const [latitude, setLatitude] = useState('')
  const [longitude, setLongitude] = useState('')
  const [busy, setBusy] = useState(false)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    void saveOffice(companyId, { office_name: name, latitude, longitude })
      .then((answer) => {
        setBusy(false)
        toast('Office created.', 'Success')
        onSaved(answer.id)
      })
      .catch((error: unknown) => {
        setBusy(false)
        toast(failed(error), 'Error', 'error')
      })
  }

  return (
    <div className="col-md-12">
      <div className="portlet light bg-inverse bordered-left border-mauve">
        <div className="portlet-title">
          <div className="caption color-mauve">
            <i className="fas fa-pencil-alt" aria-hidden="true"></i>{' '}
            <span className="caption-subject bold uppercase">General Settings</span>
          </div>
          <div className="actions">
            <button type="submit" form="add_new_office" className="btn btn-primary btn-xs" disabled={busy}>
              Save
            </button>
          </div>
        </div>
        <div className="portlet-body">
          <form method="post" className="tk-action-form" id="add_new_office" onSubmit={submit}>
            <Field id="new-office-name" label="Office Name" required>
              <input
                id="new-office-name"
                type="text"
                required
                className="form-control"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            <Field id="new-office-lat" label="Latitude">
              <input
                id="new-office-lat"
                type="number"
                className="form-control"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
              />
            </Field>
            <Field id="new-office-lon" label="Longitude">
              <input
                id="new-office-lon"
                type="number"
                className="form-control"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
              />
            </Field>
          </form>
        </div>
      </div>
    </div>
  )
}

function OfficeSettings({
  companyId,
  officeId,
  onChanged,
  onRemoved,
}: {
  companyId: number
  officeId: number
  onChanged(): void
  onRemoved(): void
}) {
  const query = useQueryClient()
  const page = useQuery(officeQuery(companyId, officeId))
  const [name, setName] = useState('')
  const [latitude, setLatitude] = useState('')
  const [longitude, setLongitude] = useState('')
  const [pick, setPick] = useState('')
  const [departments, setDepartments] = useState<string[]>([''])

  useEffect(() => {
    if (!page.data) return
    setName(page.data.office.name)
    setLatitude(page.data.office.latitude)
    setLongitude(page.data.office.longitude)
    setDepartments(
      page.data.officeDepartments.length > 0 ? page.data.officeDepartments.map((one) => String(one.ID)) : [''],
    )
  }, [page.data])

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (page.error instanceof ApiError && page.error.status === 404) {
    return <div className="alert alert-info">This office is gone.</div>
  }
  if (!page.data) return <Spinner />

  const reloadOffice = () => void query.invalidateQueries({ queryKey: ['settings', 'customers', 'office', companyId, officeId] })
  const admins = page.data.admins
  const byId = new Map(page.data.administrators.map((one) => [one.ID, one.name]))

  const adminAction = (fields: Record<string, string | number>, message: string) => {
    void saveOfficeAdmins(companyId, officeId, fields)
      .then(() => {
        toast(message, 'Success')
        reloadOffice()
        onChanged()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
  }

  return (
    <>
      <div className="col-md-12">
        <div className="portlet light bg-inverse bordered-left border-mauve">
          <div className="portlet-title">
            <div className="caption color-mauve">
              <i className="fas fa-pencil-alt" aria-hidden="true"></i>{' '}
              <span className="caption-subject bold uppercase">General Settings</span>
            </div>
            <div className="actions">
              <button type="submit" form="office_settings" className="btn btn-primary btn-xs">
                Update
              </button>{' '}
              <button
                type="button"
                className="btn btn-xs btn-danger remove_office"
                onClick={() => {
                  if (!confirm('Remove this office?')) return
                  void removeOffice(companyId, officeId)
                    .then(() => {
                      toast('Office removed.', 'Success')
                      onRemoved()
                    })
                    .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
                }}
              >
                Remove
              </button>
            </div>
          </div>
          <div className="portlet-body">
            <form
              method="post"
              className="tk-action-form"
              id="office_settings"
              onSubmit={(event) => {
                event.preventDefault()
                void saveOffice(companyId, { id: officeId, office_name: name, latitude, longitude })
                  .then(() => {
                    toast('Office updated.', 'Success')
                    onChanged()
                  })
                  .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
              }}
            >
              <Field id="office-name" label="Office Name" required>
                <input
                  id="office-name"
                  type="text"
                  required
                  className="form-control"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Field>
              <Field id="office-lat" label="Latitude">
                <input
                  id="office-lat"
                  type="number"
                  className="form-control"
                  value={latitude}
                  onChange={(e) => setLatitude(e.target.value)}
                />
              </Field>
              <Field id="office-lon" label="Longitude">
                <input
                  id="office-lon"
                  type="number"
                  className="form-control"
                  value={longitude}
                  onChange={(e) => setLongitude(e.target.value)}
                />
              </Field>
            </form>
          </div>
        </div>
      </div>

      <div className="col-md-6">
        <div className="portlet light bg-inverse bordered-left border-mauve">
          <div className="portlet-title">
            <div className="caption color-mauve">
              <i className="fas fa-pencil-alt" aria-hidden="true"></i>{' '}
              <span className="caption-subject bold uppercase">Administrators / Ticket Responsible</span>
            </div>
          </div>
          <div className="portlet-body">
            {admins.length === 0 ? (
              <div className="col-sm-12" style={{ background: '#fff', border: '1px solid #ccc', width: '100%', padding: 8 }}>
                No Admins assigned!
              </div>
            ) : (
              admins.map((one) => (
                <div
                  className="col-sm-12"
                  key={one.admin_id}
                  style={{ background: '#fff', border: '1px solid #ccc', width: '100%', padding: 8 }}
                >
                  {byId.get(one.admin_id) ?? `#${one.admin_id}`}
                  <span className="input-group-addon pull-right" style={{ background: '#fff', border: 'none', padding: '1px 0 0 3px', width: 30 }}>
                    <input
                      type="radio"
                      name={`ticket-responsible-${officeId}`}
                      aria-label="Ticket responsible"
                      checked={one.ticket_responsible === 1}
                      onChange={() => adminAction({ action: 'responsible', admin: one.admin_id }, 'Ticket responsible changed.')}
                    />
                  </span>
                  <button
                    type="button"
                    className="metro-btn xs-small bg-default color-red pull-right removeAdmin"
                    title="Remove"
                    onClick={() => adminAction({ action: 'remove', admin: one.admin_id }, 'Admin Removed!')}
                  >
                    <i className="fas fa-times" aria-hidden="true"></i>
                  </button>
                </div>
              ))
            )}
            <div className="tk-action-form" style={{ marginTop: 10 }}>
              <Field id="administratorAssign" label="Administrator">
                <SelectPicker
                  id="administratorAssign"
                  className="form-control"
                  title="Select.."
                  size={10}
                  options={page.data.administrators.map((one) => ({ value: String(one.ID), label: one.name }))}
                  value={pick}
                  onChange={setPick}
                />
              </Field>
              <SubmitRow>
                <button
                  type="button"
                  className="btn btn-primary"
                  id="addAdministratorButton"
                  onClick={() => {
                    if (pick === '') return
                    adminAction({ action: 'add', admin: Number(pick) }, 'Admin added.')
                    setPick('')
                  }}
                >
                  + Add
                </button>
              </SubmitRow>
            </div>
          </div>
        </div>
      </div>

      <div className="col-md-6">
        <div className="portlet light bg-inverse bordered-left border-mauve">
          <div className="portlet-title">
            <div className="caption color-mauve">
              <i className="fas fa-pencil-alt" aria-hidden="true"></i>{' '}
              <span className="caption-subject bold uppercase">Departments</span>
            </div>
            <div className="actions">
              <button
                type="button"
                className="btn btn-primary btn-xs update_office_departments"
                onClick={() => {
                  const wanted = departments.map((one) => Number(one)).filter((one) => one > 0)
                  void saveOfficeDepartments(companyId, officeId, wanted)
                    .then(() => {
                      toast('Departments updated.', 'Success')
                      reloadOffice()
                    })
                    .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
                }}
              >
                Update
              </button>
            </div>
          </div>
          <div className="portlet-body">
            {page.data.departments.length === 0 ? (
              <div className="alert alert-danger"> No department defined!</div>
            ) : (
              (departments.length === 0 ? [''] : departments).map((one, index) => (
                <div className="tk-action-form" key={`office-dep-${index}`}>
                  <Field id={`office-dep-${officeId}-${index}`} label={index === 0 ? 'Department' : ''}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                        <SelectPicker
                          id={`office-dep-${officeId}-${index}`}
                          className="form-control"
                          title="Select.."
                          size={10}
                          options={page.data.departments.map((dep) => ({ value: String(dep.ID), label: dep.Nume }))}
                          value={one}
                          onChange={(value) => {
                            const next = [...departments]
                            next[index] = value
                            setDepartments(next)
                          }}
                        />
                      </div>
                      <button
                        type="button"
                        className="metro-btn xs-small bg-default color-red removeInput"
                        title="Remove"
                        onClick={() => setDepartments(departments.filter((_, i) => i !== index))}
                      >
                        <i className="fas fa-times" aria-hidden="true"></i>
                      </button>
                      <button
                        type="button"
                        className="metro-btn xs-small bg-default color-green2 addInput"
                        title="Add"
                        onClick={() => setDepartments([...departments.slice(0, index + 1), '', ...departments.slice(index + 1)])}
                      >
                        <i className="fas fa-plus" aria-hidden="true"></i>
                      </button>
                    </div>
                  </Field>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="clearfix"></div>

      <div className="col-md-6">
        <OfficeIpsPanel officeId={officeId} />
      </div>
      <div className="col-md-6">
        <OfficeNetworksPanel officeId={officeId} />
      </div>
    </>
  )
}

/** `others/office_ips.php`: adresele publice prin care iese sediul. */
function OfficeIpsPanel({ officeId }: { officeId: number }) {
  const query = useQueryClient()
  const page = useQuery(officeIpsQuery(officeId))
  const [value, setValue] = useState('')
  const [error, setError] = useState('')

  if (!page.data) return <Spinner />

  const reload = () => void query.invalidateQueries({ queryKey: ['settings', 'customers', 'office-ips', officeId] })

  return (
    <div className="portlet light bg-inverse bordered-left border-mauve" id="officeIpsPanel">
      <div className="portlet-title">
        <div className="caption color-mauve">
          <i className="fas fa-globe" aria-hidden="true"></i>{' '}
          <span className="caption-subject bold uppercase">External</span>{' '}
          <span className="caption-helper">what this office looks like from the outside</span>
        </div>
      </div>
      <div className="portlet-body">
        <p className="text-muted small">
          An agent reporting from one of these is taken to be at this office - the portal then offers this office when
          the machine has none. Nothing is written without somebody pressing Add. This is the better of the two: two
          offices almost surely go out through different addresses, while the internal ones repeat. Internal is only
          read when this says nothing.
        </p>
        {error !== '' ? <div className="alert alert-danger">{error}</div> : null}
        <table className="table table-condensed" id="officeIpTable">
          <tbody>
            {page.data.ips.length === 0 ? (
              <tr className="office-ip-empty">
                <td colSpan={2} className="text-muted">
                  No address recorded, so nothing is ever matched to this office from the outside.
                </td>
              </tr>
            ) : (
              page.data.ips.map((one) => (
                <tr key={one.ID}>
                  <td>
                    <code>{one.ip}</code>
                  </td>
                  <td className="text-right" style={{ width: 60 }}>
                    {page.data.canWrite ? (
                      <button
                        type="button"
                        className="btn btn-xs btn-danger office-ip-remove"
                        title="Remove"
                        onClick={() => {
                          void saveOfficeIp(officeId, { action: 'remove', id: one.ID })
                            .then(() => {
                              toast('Removed.', 'Success')
                              reload()
                            })
                            .catch((err: unknown) => setError(failed(err)))
                        }}
                      >
                        <i className="fas fa-times" aria-hidden="true"></i>
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {page.data.canWrite ? (
          <div className="form-inline">
            <input
              type="text"
              className="form-control input-sm"
              id="officeIpNew"
              placeholder="e.g. 81.196.10.4"
              aria-label="External address"
              maxLength={45}
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-sm btn-primary"
              id="officeIpAdd"
              onClick={() => {
                setError('')
                void saveOfficeIp(officeId, { ip: value })
                  .then(() => {
                    setValue('')
                    toast('Added.', 'Success')
                    reload()
                  })
                  .catch((err: unknown) => setError(failed(err)))
              }}
            >
              <i className="fas fa-plus" aria-hidden="true"></i> Add
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )
}

/** `others/office_networks.php`: retelele interne ale sediului. */
function OfficeNetworksPanel({ officeId }: { officeId: number }) {
  const query = useQueryClient()
  const page = useQuery(officeNetworksQuery(officeId))
  const [value, setValue] = useState('')
  const [error, setError] = useState('')

  if (!page.data) return <Spinner />

  const reload = () => void query.invalidateQueries({ queryKey: ['settings', 'customers', 'office-networks', officeId] })

  return (
    <div className="portlet light bg-inverse bordered-left border-mauve" id="officeNetworksPanel">
      <div className="portlet-title">
        <div className="caption color-mauve">
          <i className="fas fa-network-wired" aria-hidden="true"></i>{' '}
          <span className="caption-subject bold uppercase">Internal</span>{' '}
          <span className="caption-helper">what this office looks like from the inside</span>
        </div>
      </div>
      <div className="portlet-body">
        {!page.data.tableHere ? (
          <div className="alert alert-info">
            <i className="fas fa-info-circle" aria-hidden="true"></i> The table is not created yet - run the
            create_it_office_networks migration (migrations project).
          </div>
        ) : null}
        {error !== '' ? <div className="alert alert-danger">{error}</div> : null}
        <table className="table table-condensed" id="officeNetworkTable">
          <tbody>
            {page.data.networks.length === 0 ? (
              <tr>
                <td colSpan={2} className="text-muted">
                  No network recorded. Internal addresses repeat between offices, so this is only read when the external
                  one says nothing.
                </td>
              </tr>
            ) : (
              page.data.networks.map((one) => (
                <tr key={one.ID}>
                  <td>
                    <code>{one.cidr}</code>
                  </td>
                  <td className="text-right" style={{ width: 60 }}>
                    {page.data.canWrite ? (
                      <button
                        type="button"
                        className="btn btn-xs btn-danger office-network-remove"
                        title="Remove"
                        onClick={() => {
                          void saveOfficeNetwork(officeId, { action: 'remove', id: one.ID })
                            .then(() => {
                              toast('Removed.', 'Success')
                              reload()
                            })
                            .catch((err: unknown) => setError(failed(err)))
                        }}
                      >
                        <i className="fas fa-times" aria-hidden="true"></i>
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {page.data.canWrite && page.data.tableHere ? (
          <div className="form-inline">
            <input
              type="text"
              className="form-control input-sm"
              id="officeNetworkNew"
              placeholder="e.g. 10.120.50.0/24"
              aria-label="Internal network"
              maxLength={45}
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-sm btn-primary"
              id="officeNetworkAdd"
              onClick={() => {
                setError('')
                void saveOfficeNetwork(officeId, { network: value })
                  .then((answer) => {
                    setValue('')
                    toast(answer.message ?? 'Added.', 'Success')
                    reload()
                  })
                  .catch((err: unknown) => setError(failed(err)))
              }}
            >
              <i className="fas fa-plus" aria-hidden="true"></i> Add
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )
}

/** `companies/settings.php`: patru formulare pe aceeasi fila. */
/**
 * "Change Logo" - `Customers::changeCompanyLogo`, modala
 * `customers/companies/others/change_logo.php`: un selector de fisier si
 * "Update". Decuparea (`x/y/w/h`, `cropImage`) era comentata si in portal.
 *
 * SELECTORUL DE FISIER NU SE MAI SCRIE CU `input-upload`. In macheta veche,
 * campul statea INVIZIBIL (`app.css`: `opacity: 0`, `position: absolute`,
 * `font-size: 1px`, `transform: scale(4)`) peste butonul desenat de pluginul
 * bootstrap-fileinput (`<span class="btn btn-file">Select image upload</span>`,
 * intr-un `div.fileinput` cu `data-provides="fileinput"`). v4 a portat campul
 * fara invelisul lui si fara pluginul care il desena, deci in fereastra nu se
 * vedea NIMIC de apasat, iar "Update" ramanea stins la nesfarsit
 * (`disabled={... || !file}`): sigla unei firme nu se putea schimba deloc.
 *
 * Acum e eticheta-buton de la incarcarile de imagine din Settings -> Company
 * (`CompanySettingsPage`): `label.btn.btn-default.upload-btn` peste un
 * `input.hide`. Campul pastreaza numele `company_logo`, cel pe care il citeste
 * `SettingsCustomersController::companyLogo`.
 */
export function ChangeLogoModal({ companyId, onClose, onSaved }: { companyId: number; onClose(): void; onSaved(): void }) {
  const [file, setFile] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!file) return
    setBusy(true)
    void uploadCustomerLogo(companyId, file)
      .then(() => {
        setBusy(false)
        toast('Company logo updated.', 'Success')
        onSaved()
      })
      .catch((error: unknown) => {
        setBusy(false)
        toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error')
      })
  }

  return (
    <Modal id="modal" open onClose={onClose} title="Change company logo">
      <form method="post" className="form-horizontal" id="company_logo" encType="multipart/form-data" onSubmit={submit}>
        <div className="form-group">
          <label className="col-sm-3 control-label" htmlFor="uploadImage">
            Logo
          </label>
          <div className="col-sm-9">
            <label className="btn btn-default upload-btn" htmlFor="uploadImage" style={{ marginBottom: 0 }}>
              <i className="far fa-image" aria-hidden="true"></i> Select image upload{file ? ': ' + file.name : ''}
            </label>
            <input
              id="uploadImage"
              className="hide"
              type="file"
              name="company_logo"
              accept="image/*"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <span className="help-block">Please upload only jpg, jpeg and png file formats.</span>
          </div>
        </div>
        <div className="form-group">
          <div className="col-sm-offset-3 col-sm-9 aright">
            <button type="button" className="btn btn-default" onClick={onClose}>
              Close
            </button>{' '}
            <button type="submit" className="btn btn-primary update_company_logo" disabled={busy || !file}>
              Update
            </button>
          </div>
        </div>
      </form>
    </Modal>
  )
}

/**
 * Fila "Contracts" (`settings/customer_contracts/index/{firma}`): lista de
 * contracte a ecranului Contracts, doar pentru firma asta. Formularul de
 * contract si documentele sunt aceleasi ca acolo.
 */
function ContractsTab({ companyId }: { companyId: number }) {
  const shell = useQuery(contractsShellQuery)
  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />
  if (!shell.data) return <Spinner />
  return <ContractsPanel shell={shell.data} company={companyId} />
}

function CompanySettingsTab({ companyId, onStatus }: { companyId: number; onStatus(enable: boolean): void }) {
  const [logoOpen, setLogoOpen] = useState(false)
  const [logoStamp, setLogoStamp] = useState(0)
  const query = useQueryClient()
  const page = useQuery(companySettingsQuery(companyId))
  const [form, setForm] = useState({
    contact_person: '',
    it_manager: '',
    language: 'EN',
    timezone: '0',
    unique_registartion: '',
    tax_identification: '',
    latitude: '',
    longitude: '',
    working_program: '',
  })
  const [premium, setPremium] = useState(false)
  const [consentSettings, setConsentSettings] = useState(false)
  const [consentAll, setConsentAll] = useState(false)
  const [reports, setReports] = useState(false)
  const [externalProvider, setExternalProvider] = useState(false)
  const [domains, setDomains] = useState<string[]>([''])
  const [cnrs, setCnrs] = useState<number[]>([])
  const [cnrTop, setCnrTop] = useState(0)
  const [vips, setVips] = useState<number[]>([])

  useEffect(() => {
    if (!page.data) return
    const one = page.data.company
    setForm({
      contact_person: one.contactPerson,
      it_manager: one.itManager > 0 ? String(one.itManager) : '',
      language: one.language === 'RO' ? 'RO' : 'EN',
      timezone: String(one.timezone),
      unique_registartion: one.uniqueRegistration,
      tax_identification: one.taxIdentification,
      latitude: one.latitude,
      longitude: one.longitude,
      working_program: one.workingProgram,
    })
    /* Bifa "Premium Company" trimite 0 si e bifata cand coloana e 0 - da,
       invers, exact ca in macheta. */
    setPremium(one.premium === 0)
    setConsentSettings(one.disableRemoteConsent === 1)
    setConsentAll(one.alwaysDisableConsent === 0)
    setReports(one.enableReporting === 1)
    setExternalProvider(one.externalProvider === 1)
    setDomains(page.data.domains.length > 0 ? page.data.domains : [''])
    setCnrs(page.data.cnrs.map((x) => x.id))
    setCnrTop(page.data.cnrs.find((x) => x.top === 1)?.id ?? 0)
    setVips(page.data.vips.map((x) => x.id))
  }, [page.data])

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />

  const reload = () => void query.invalidateQueries({ queryKey: ['settings', 'customers'] })
  const locked = page.data.consentLocked

  const submitSettings = (event: FormEvent) => {
    event.preventDefault()
    const fields: Record<string, string | number> = { ...form }
    if (premium) fields.premium = 0
    if (reports) fields.enable_company_reports = 1
    if (externalProvider) fields.external_provider = 1
    if (!locked) {
      if (consentSettings) fields.remote_consent_settings = 1
      if (consentAll) fields.remote_consent_enable = 0
    }
    void saveCompanySettings(companyId, fields)
      .then(() => {
        toast('Company updated.', 'Success')
        reload()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
  }

  const text = (name: keyof typeof form, label: string, type = 'text') => (
    <Field id={`company-${name}`} label={label}>
      <input
        id={`company-${name}`}
        type={type}
        className="form-control"
        value={form[name]}
        onChange={(e) => setForm({ ...form, [name]: e.target.value })}
      />
    </Field>
  )

  const check = (id: string, label: string, checked: boolean, onChange: (next: boolean) => void, disabled = false) => (
    <div className="checkbox">
      <label htmlFor={id}>
        <input id={id} type="checkbox" disabled={disabled} checked={checked} onChange={(e) => onChange(e.target.checked)} />{' '}
        {label}
      </label>
    </div>
  )

  return (
    <div className="row" id="companie_settings">
      <div className="col-md-12" style={{ marginBottom: 15 }}>
        <h4 className="bordered">
          <b>
            {page.data.company.logoUrl !== '' ? (
              <span className="company-thumb-settings">
                <img
                  src={logoStamp > 0 ? page.data.company.logoUrl + '?t=' + logoStamp : page.data.company.logoUrl}
                  alt=""
                  onError={(event) => {
                    event.currentTarget.style.display = 'none'
                  }}
                />
              </span>
            ) : null}
            {page.data.company.name}
          </b>
          <button className="btn btn-primary change_company_logo" type="button" onClick={() => setLogoOpen(true)}>
            {' '}
            Change Logo
          </button>
        </h4>
        {logoOpen ? (
          <ChangeLogoModal
            companyId={companyId}
            onClose={() => setLogoOpen(false)}
            onSaved={() => {
              setLogoOpen(false)
              setLogoStamp(Date.now())
            }}
          />
        ) : null}
      </div>

      <div className="col-md-12">
        <div className="portlet light bg-inverse bordered-left border-mauve">
          <div className="portlet-title">
            <div className="caption color-mauve">
              <i className="fas fa-pencil-alt" aria-hidden="true"></i>{' '}
              <span className="caption-subject bold uppercase">General Settings</span>
            </div>
            <div className="actions">
              <button type="submit" form="company_settings" className="btn x-small btn-primary update_company_settings">
                Update
              </button>{' '}
              <button
                type="button"
                className={page.data.company.enabled ? 'btn x-small btn-danger change_company_status' : 'btn x-small btn-green change_company_status'}
                onClick={() => onStatus(!page.data.company.enabled)}
              >
                {page.data.company.enabled ? 'Disable' : 'Enable'}
              </button>
            </div>
          </div>
          <div className="portlet-body">
            <form method="post" className="tk-action-form" id="company_settings" onSubmit={submitSettings}>
              {text('contact_person', 'Contact Person:')}
              <Field id="company-it-manager" label="IT Manager:">
                <SelectPicker
                  id="company-it-manager"
                  className="form-control"
                  title="Select.."
                  size={10}
                  options={page.data.itManagers.map((one) => ({ value: String(one.ID), label: one.name }))}
                  value={form.it_manager}
                  onChange={(value) => setForm({ ...form, it_manager: value })}
                />
              </Field>
              <Field id="company-language" label="Language:">
                <SelectPicker
                  id="company-language"
                  className="form-control"
                  title="Select.."
                  options={[
                    { value: 'EN', label: 'EN' },
                    { value: 'RO', label: 'RO' },
                  ]}
                  value={form.language}
                  onChange={(value) => setForm({ ...form, language: value })}
                />
              </Field>
              <Field id="company-timezone" label="Timezone:">
                <SelectPicker
                  id="company-timezone"
                  className="form-control"
                  title="Select company timezone..."
                  size={10}
                  options={page.data.timezones.map((one) => ({ value: String(one.id), label: one.timezone }))}
                  value={form.timezone === '0' ? '' : form.timezone}
                  onChange={(value) => setForm({ ...form, timezone: value === '' ? '0' : value })}
                />
              </Field>
              {text('unique_registartion', 'Unique registration code:')}
              {text('tax_identification', 'Tax identification number:')}
              {text('latitude', 'Latitude:', 'number')}
              {text('longitude', 'Longitude:', 'number')}
              <Field id="company-program" label="Working program">
                <textarea
                  id="company-program"
                  className="form-control"
                  rows={4}
                  value={form.working_program}
                  onChange={(e) => setForm({ ...form, working_program: e.target.value })}
                />
              </Field>
              <Field label="Other Settings">
                <div>
                  {check('company-premium', 'Premium Company', premium, setPremium)}
                  {check('company-consent-settings', 'Enable Remote Consent Settings', consentSettings, setConsentSettings, locked)}
                  {check('company-consent-all', 'Enable Remote Consent For All Devices', consentAll, setConsentAll, locked)}
                  {check('company-reports', 'Enable Company Reports', reports, setReports)}
                  {check('company-external-provider', 'External provider', externalProvider, setExternalProvider)}
                </div>
              </Field>
            </form>
          </div>
        </div>
      </div>

      <div className="col-md-6">
        <PeopleBox
          title="Critical notification receiver / Remote connect approval"
          companyId={companyId}
          chosen={cnrs}
          onChosen={setCnrs}
          top={cnrTop}
          onTop={setCnrTop}
          onSave={() =>
            void saveCnrs(companyId, cnrs, cnrTop)
              .then(() => {
                toast('Saved.', 'Success')
                reload()
              })
              .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
          }
        />
      </div>

      <div className="col-md-6">
        <PeopleBox
          title="VIP"
          companyId={companyId}
          chosen={vips}
          onChosen={setVips}
          onSave={() =>
            void saveVips(companyId, vips)
              .then(() => {
                toast('Saved.', 'Success')
                reload()
              })
              .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
          }
        />
      </div>

      <div className="col-md-12">
        <div className="portlet light bg-inverse bordered-left border-mauve">
          <div className="portlet-title">
            <div className="caption color-mauve">
              <i className="fa fa-tasks" aria-hidden="true"></i>{' '}
              <span className="caption-subject bold uppercase">Domains</span>
            </div>
            <div className="actions">
              <button
                type="button"
                className="btn btn-xs btn-primary update_company_domains"
                onClick={() =>
                  void saveDomains(companyId, domains)
                    .then((answer) => {
                      setDomains(answer.domains.length > 0 ? answer.domains : [''])
                      toast('Domains saved.', 'Success')
                    })
                    .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
                }
              >
                Update
              </button>
            </div>
          </div>
          <div className="portlet-body">
            {/* `od-form-row` (v4.css): campul cu butoane alaturi ramane un
                `input-group` intreg pe latime, nu unul stramtat de `form-inline`. */}
            <div className="od-form-row">
              <RepeatedInputs values={domains} onChange={setDomains} placeholder="domain.com" name="domains" />
            </div>
          </div>
        </div>
      </div>

      {page.data.siem ? (
        <div className="col-md-12">
          <CompanySiemGroupCard key={page.data.siem.group?.ID ?? 0} companyId={companyId} companyName={page.data.company.name} card={page.data.siem} />
        </div>
      ) : null}
    </div>
  )
}

/**
 * Cutia de oameni (CNR sau VIP): cauta, alege, si - la CNR - spune care e
 * primul. In portal era un `select2` cu ajax pe `general/getOwnersByCompanyAjax`;
 * capatul e acelasi, doar ca acum e al ecranului.
 */
function PeopleBox({
  title,
  companyId,
  chosen,
  onChosen,
  top,
  onTop,
  onSave,
}: {
  title: string
  companyId: number
  chosen: number[]
  onChosen(next: number[]): void
  top?: number
  onTop?: (id: number) => void
  onSave(): void
}) {
  const [search, setSearch] = useState('')
  const found = useQuery(peopleQuery(companyId, search))
  const known = new Map((found.data?.people ?? []).map((one) => [one.id, one.text]))

  return (
    <div className="portlet light bg-inverse bordered-left border-mauve">
      <div className="portlet-title">
        <div className="caption color-mauve">
          <i className="fas fa-pencil-alt" aria-hidden="true"></i>{' '}
          <span className="caption-subject bold uppercase">{title}</span>
        </div>
        <div className="actions">
          <button type="button" className="btn btn-xs btn-primary" onClick={onSave}>
            Update
          </button>
        </div>
      </div>
      <div className="portlet-body">
        {chosen.length === 0 ? (
          <p className="text-muted small">Nobody picked yet.</p>
        ) : (
          chosen.map((id) => (
            <div className="input-group" key={id}>
              <span className="form-control small">{known.get(id) ?? `#${id}`}</span>
              <span className="input-group-btn">
                <span className="input-group-addon" style={{ width: 34, height: 34, background: '#fff', border: '1px solid #ccc' }}>
                  <button
                    type="button"
                    className="metro-btn xs-small bg-default color-red removeInput"
                    title="Remove"
                    onClick={() => onChosen(chosen.filter((one) => one !== id))}
                  >
                    <i className="fas fa-times" aria-hidden="true"></i>
                  </button>
                </span>
              </span>
              {onTop ? (
                <span className="input-group-btn">
                  <span className="input-group-addon" style={{ width: 34, height: 34, background: '#fff', border: '1px solid #ccc' }}>
                    <input
                      type="radio"
                      name={`cnr_top_${companyId}`}
                      aria-label="Top"
                      checked={top === id}
                      onChange={() => onTop(id)}
                    />
                  </span>
                </span>
              ) : null}
            </div>
          ))
        )}
        <div className="od-form-row" style={{ marginTop: 8 }}>
          <input
            type="text"
            className="form-control input-sm"
            placeholder="Search or pick a person"
            aria-label={`Search people for ${title}`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {/* Lista ramane un <select size> (o lista de ales, nu un sertar):
            cautarea e pe server, prin casuta de deasupra. */}
        <select
          className="form-control small owner_list_autocomplete"
          style={{ display: 'block', width: '100%', height: 'auto', marginTop: 6 }}
          aria-label={`People for ${title}`}
          size={5}
          value=""
          onChange={(e) => {
            const id = Number(e.target.value)
            if (id > 0 && !chosen.includes(id)) onChosen([...chosen, id])
          }}
        >
          {(found.data?.people ?? []).map((one) => (
            <option key={one.id} value={one.id}>
              {one.text}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}

/** `companies/agent_settings.php`: o singura setare, culoarea agentului. */
function AgentSettingsTab({ companyId }: { companyId: number }) {
  const page = useQuery(agentSettingsQuery(companyId))
  const [color, setColor] = useState('')

  useEffect(() => {
    if (page.data) setColor(page.data.mainColor)
  }, [page.data])

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />

  return (
    <div className="row">
      <div className="col-md-12">
        <div className="portlet light bg-inverse bordered-left border-red">
          <div className="portlet-title">
            <div className="caption color-red">
              <i className="fas fa-pencil-alt" aria-hidden="true"></i>{' '}
              <span className="caption-subject bold uppercase">General settings</span>
            </div>
            <div className="actions">
              <button
                type="button"
                className="btn x-small btn-primary update_general_settings"
                onClick={() =>
                  void saveAgentSettings(companyId, color)
                    .then(() => toast('Agent settings saved.', 'Success'))
                    .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
                }
              >
                Update
              </button>
            </div>
          </div>
          <div className="portlet-body">
            <div className="tk-action-form">
              <Field id="agent-color" label="Agent main color:">
                <input
                  id="agent-color"
                  type="text"
                  className="form-control"
                  style={{ borderLeft: `40px solid ${color === '' ? '#DDDDDD' : color}` }}
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                />
              </Field>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/** `companies/modules.php`. */
function ModulesTab({ companyId }: { companyId: number }) {
  const page = useQuery(companyModulesQuery(companyId))
  const [chosen, setChosen] = useState<number[]>([])
  /* Un singur "Update" pe drum: doua trimise deodata scriau fiecare modul de doua ori. */
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (page.data) setChosen(page.data.assigned)
  }, [page.data])

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />

  return (
    <div className="tk-action-form">
      <Field label="Modules">
        {/* flex, nu grid: `.tk-action-form .checkbox { grid-column: 2 }` ar
            prinde bifele si intr-o grila interioara. */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 15px' }}>
          {page.data.modules.map((one) => (
            <div className="checkbox" key={one.id} style={{ flex: '0 0 220px' }}>
              <label htmlFor={`company-module-${one.id}`}>
                <input
                  id={`company-module-${one.id}`}
                  type="checkbox"
                  checked={chosen.includes(one.id)}
                  onChange={(e) => setChosen(e.target.checked ? [...chosen, one.id] : chosen.filter((x) => x !== one.id))}
                />{' '}
                {one.name}
              </label>
            </div>
          ))}
        </div>
      </Field>
      <SubmitRow>
        <button
          type="button"
          className="btn btn-primary"
          id="update_modules"
          disabled={saving}
          onClick={() => {
            if (saving) return
            setSaving(true)
            void saveCompanyModules(companyId, chosen)
              .then(() => toast('Modules saved.', 'Success'))
              .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
              .finally(() => setSaving(false))
          }}
        >
          Update
        </button>
      </SubmitRow>
    </div>
  )
}

/** `2fa/index.php`: comutatorul si lista de adrese scutite. */
function TwoFactorTab({ companyId }: { companyId: number }) {
  const query = useQueryClient()
  const page = useQuery(twoFactorQuery(companyId))
  const [ips, setIps] = useState<string[]>([])

  useEffect(() => {
    if (page.data) setIps(page.data.ips.length > 0 ? page.data.ips : [''])
  }, [page.data])

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />

  const reload = () => void query.invalidateQueries({ queryKey: ['settings', 'customers', 'two-factor', companyId] })

  return (
    <div className="tk-action-form">
      <h4>Enable Two Factor Authentication</h4>
      <hr className="dotted" />
      <Field label="Two factor authentication">
        <div className="checkbox">
          <label htmlFor={`two-factor-${companyId}`}>
            <input
              id={`two-factor-${companyId}`}
              className="switchery"
              type="checkbox"
              checked={page.data.enabled}
              onChange={(e) =>
                void setTwoFactorStatus(companyId, e.target.checked)
                  .then(() => {
                    toast(e.target.checked ? 'Two factor authentication on.' : 'Two factor authentication off.', 'Success')
                    reload()
                  })
                  .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
              }
            />{' '}
            Enabled
          </label>
        </div>
      </Field>

      <h4>Disable Two Factor Authentication IP List</h4>
      <hr className="dotted" />
      <div data-repeater-list="ip-list">
        {ips.map((one, index) => (
          <Field id={`2fa-ip-${companyId}-${index}`} label={index === 0 ? 'IP list' : ''} key={`2fa-ip-${index}`}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <div className="od-form-row" style={{ flex: '1 1 auto', minWidth: 0 }}>
                <div className="input-group">
                  <div className="input-group-addon">IP</div>
                  <input
                    id={`2fa-ip-${companyId}-${index}`}
                    type="text"
                    className="form-control"
                    /* celula de tabel a input-group-ului, peste `display: block` al grilei */
                    style={{ display: 'table-cell' }}
                    aria-label="IP"
                    value={one}
                    onChange={(e) => {
                      const next = [...ips]
                      next[index] = e.target.value
                      setIps(next)
                    }}
                  />
                </div>
              </div>
              <button type="button" className="btn btn-danger btn-sm" onClick={() => setIps(ips.filter((_, i) => i !== index))}>
                Delete
              </button>
            </div>
          </Field>
        ))}
      </div>
      <Field label="">
        <div>
          <button type="button" className="btn btn-primary btn-sm data-repeater" onClick={() => setIps([...ips, ''])}>
            <i className="fas fa-plus" aria-hidden="true"></i> Add
          </button>
        </div>
      </Field>
      <SubmitRow>
        <button
          type="button"
          className="btn btn-success"
          onClick={() =>
            void saveTwoFactorIps(companyId, ips.filter((one) => one.trim() !== ''))
              .then(() => {
                toast('IP list saved.', 'Success')
                reload()
              })
              .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
          }
        >
          Save
        </button>
      </SubmitRow>
    </div>
  )
}

/** `companies/billing.php`. */
function BillingTab({ companyId }: { companyId: number }) {
  const page = useQuery(billingQuery(companyId))
  const [form, setForm] = useState<BillingForm>({
    nrc: '',
    fiscal_code: '',
    iban: '',
    bank: '',
    county: '',
    contact_email: '',
    company_address: '',
  })
  const [error, setError] = useState('')

  useEffect(() => {
    if (page.data) setForm(page.data.billing)
  }, [page.data])

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />

  const row = (name: keyof BillingForm, label: string) => (
    <Field id={`billing-${name}`} label={label} required key={name}>
      {name === 'company_address' ? (
        <textarea
          id={`billing-${name}`}
          className="form-control"
          required
          value={form[name]}
          onChange={(e) => setForm({ ...form, [name]: e.target.value })}
        />
      ) : (
        <input
          id={`billing-${name}`}
          type="text"
          className="form-control"
          required
          value={form[name]}
          onChange={(e) => setForm({ ...form, [name]: e.target.value })}
        />
      )}
    </Field>
  )

  return (
    <div>
      <form
        method="post"
        className="tk-action-form"
        id="update_billing_form"
        onSubmit={(event) => {
          event.preventDefault()
          setError('')
          void saveBilling(companyId, { ...form })
            .then(() => toast('Billing data saved.', 'Success'))
            .catch((err: unknown) => setError(failed(err)))
        }}
      >
        {error !== '' ? <div className="alert alert-danger">{error}</div> : null}
        {row('nrc', 'NRC')}
        {row('fiscal_code', 'Fiscal Code')}
        {row('iban', 'IBAN')}
        {row('bank', 'Bank')}
        {row('county', 'County')}
        {row('contact_email', 'Email')}
        {row('company_address', 'Address')}
        <SubmitRow>
          <button type="submit" className="btn btn-primary" id="update_billing">
            Update
          </button>
        </SubmitRow>
      </form>
    </div>
  )
}

/* -------------------------------------------------------------------------
 * Customer Groups
 * ---------------------------------------------------------------------- */

function GroupsPanel() {
  const query = useQueryClient()
  const [state, setState] = useTableState(GROUPS_TABLE, {
    start: 0,
    length: 25,
    /* `aaSorting: [[1, 'asc']]` din customers.js. */
    order: { column: 1, dir: 'asc' },
    search: '',
  })
  const columns = useMemo(() => groupColumns(), [])
  const [creating, setCreating] = useState(false)

  const table = useQuery({
    ...groupsQuery({
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: state.order.column,
      orderName: GROUP_ORDER_NAMES[state.order.column] ?? 'it_companies_groups.group_name',
      orderDir: state.order.dir,
      search: state.search,
    }),
    placeholderData: keepPreviousData,
  })

  if (table.error instanceof ApiError && table.error.status === 403) return <Forbidden />

  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]
  const reload = () => void query.invalidateQueries({ queryKey: ['settings', 'customers'] })

  return (
    <div>
      <button type="button" className="pull-right btn btn-success btn-sm" onClick={() => setCreating(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Create group
      </button>
      <h4>
        <strong>Customer Groups</strong>
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage <strong>groups</strong>
      </label>
      <DataTable
        id={GROUPS_TABLE}
        columns={columns}
        rows={rows}
        total={table.data?.recordsFiltered ?? 0}
        fetching={table.isFetching}
        error={table.error ? table.error.message : null}
        onRetry={() => void table.refetch()}
        state={state}
        onState={setState}
        rowId={(row) => String(row.DT_RowId ?? `row_${Number(row.ID)}`)}
        emptyHint="A group is a handful of customer companies you look at together."
        details={(row) => <GroupPanel groupId={Number(row.ID)} onChanged={reload} />}
      />
      <CreateGroupModal open={creating} onClose={() => setCreating(false)} onSaved={reload} />
    </div>
  )
}

/** `groups/modals.php`. */
function CreateGroupModal({ open, onClose, onSaved }: { open: boolean; onClose(): void; onSaved(): void }) {
  const [name, setName] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (open) {
      setName('')
      setError('')
    }
  }, [open])

  return (
    <Modal id="addGroupModal" open={open} onClose={onClose} title="Create new group">
      <form
        className="form-horizontal"
        id="saveGroupForm"
        onSubmit={(event) => {
          event.preventDefault()
          setError('')
          void saveGroup({ group_name: name })
            .then(() => {
              toast('Group created!', 'Saved')
              onSaved()
              onClose()
            })
            .catch((err: unknown) => setError(failed(err)))
        }}
      >
        {error !== '' ? <div className="alert alert-danger">{error}</div> : null}
        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: 'left' }} htmlFor="new-group-name">
            <span className="color">Group name</span>
          </label>
          <div className="col-sm-9">
            <input
              id="new-group-name"
              className="form-control"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
        </div>
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button type="submit" className="btn btn-primary goCreateGroup">
            Save
          </button>
        </div>
      </form>
    </Modal>
  )
}

/** `groups/others/{groupDetails,groupSettings}.php`, cele doua file ale randului. */
function GroupPanel({ groupId, onChanged }: { groupId: number; onChanged(): void }) {
  const query = useQueryClient()
  const page = useQuery(groupQuery(groupId))
  const [tab, setTab] = useState<'details' | 'settings'>('details')
  const [name, setName] = useState('')
  const [chosen, setChosen] = useState<number[]>([])

  useEffect(() => {
    if (!page.data) return
    setName(page.data.group.name)
    setChosen(page.data.companies.filter((one) => one.selected === 1).map((one) => one.ID))
  }, [page.data])

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />

  const reload = () => {
    void query.invalidateQueries({ queryKey: ['settings', 'customers'] })
    onChanged()
  }

  return (
    <div className="row d-flex m-0">
      <div className="col-auto bg-lightest-gray">
        <ul className="action_tabs">
          {(['details', 'settings'] as const).map((one) => (
            <li key={one}>
              <a
                href="/app/settings/customers/groups"
                className={one === tab ? 'action_button active' : 'action_button'}
                onClick={(e) => {
                  e.preventDefault()
                  setTab(one)
                }}
              >
                {one === 'details' ? 'Details' : 'Settings'}
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className="col-md-12">
        <div className="details_content">
          {tab === 'details' ? (
            <table style={{ width: '100%' }} className="details-table rowDetails">
              <tbody>
                <tr>
                  <td className="title rowDetails">Group name:</td>
                  <td className="rowDetails">{page.data.group.name.trim() !== '' ? page.data.group.name : <em>n/a</em>}</td>
                </tr>
                <tr className="rowDetails">
                  <td className="title rowDetails">Companies:</td>
                  <td className="rowDetails">
                    {page.data.companies.filter((one) => one.selected === 1).length === 0 ? (
                      <em>n/a</em>
                    ) : (
                      page.data.companies
                        .filter((one) => one.selected === 1)
                        .map((one) => <p key={one.ID}>{one.Nume}</p>)
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <>
              <header className="panel-heading sm">
                <h4>
                  <strong>Group</strong> settings
                </h4>
              </header>
              <div className="panel-body">
                <form
                  className="tk-action-form updateGroupDetails"
                  onSubmit={(event) => {
                    event.preventDefault()
                    void saveGroup({ id: groupId, group_name: name })
                      .then(() => {
                        toast('Group updated.', 'Success')
                        reload()
                      })
                      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
                  }}
                >
                  <Field id={`group-name-${groupId}`} label="Group name">
                    <input
                      id={`group-name-${groupId}`}
                      className="form-control"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </Field>
                  <SubmitRow>
                    <button type="submit" className="btn btn-theme" disabled={!page.data.canWrite}>
                      Update
                    </button>
                  </SubmitRow>
                </form>
              </div>

              <header className="panel-heading sm">
                <h4>
                  <strong>Group</strong> companies
                </h4>
              </header>
              <div className="panel-body">
                <div className="tk-action-form">
                  <Field id={`group-companies-${groupId}`} label="Companies">
                    {/* "Check All" / "Uncheck All" sunt cele doua butoane ale
                        sertarului (`actionsBox`), pe ce a ramas dupa cautare. */}
                    <SelectPicker
                      id={`group-companies-${groupId}`}
                      className="form-control"
                      title="Select companies..."
                      multiple
                      actionsBox
                      selectedTextFormat="count > 3"
                      size={10}
                      options={page.data.companies.map((one) => ({ value: String(one.ID), label: one.Nume }))}
                      value={chosen.map(String)}
                      onChange={(values) => setChosen(values.map(Number))}
                    />
                  </Field>
                  <SubmitRow>
                    <button
                      type="button"
                      className="btn btn-theme"
                      disabled={!page.data.canWrite}
                      onClick={() =>
                        void saveGroupCompanies(groupId, chosen)
                          .then(() => {
                            toast('Group companies updated.', 'Success')
                            reload()
                          })
                          .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
                      }
                    >
                      Update
                    </button>
                  </SubmitRow>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
